/**
 * "Add to portfolio" on a coaching result.
 *
 * Explicit, two-step, owner-only:
 *  1. "Add to portfolio" creates a PRIVATE portfolio item (only the owner sees it).
 *  2. "Publish on my portfolio" is the existing per-item publication control
 *     (the same upsertHighlight isPublic toggle the Portfolio page uses), and
 *     can be undone the same way.
 * Nothing here publishes automatically, makes the profile public, or opts the
 * user into talent discovery — that stays a separate setting.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetMyHighlightsQuery, useUpsertHighlightMutation, type PortfolioHighlight, type UpsertHighlightRequest } from '../../store/api/portfolioApi';
import { useAddResultToPortfolioMutation } from '../../store/api/portfolioResultApi';

export const ADD_TO_PORTFOLIO_LABEL = 'Add to portfolio';
export const PRIVATE_ITEM_NOTE = 'In your portfolio, private. Only you can see it until you publish it.';
export const PUBLISHED_ITEM_NOTE = 'Published on your portfolio.';
export const DISCOVERY_NOTE = 'Publishing an item does not make you discoverable to recruiters; talent discovery is a separate setting.';

/**
 * The existing per-item publication write for one item: flips isPublic and
 * nothing else (same source; the server refuses retargeting a result item).
 */
export function publishToggleRequest(h: Pick<PortfolioHighlight, 'id' | 'title' | 'sourceType' | 'sourceId' | 'isPublic'>): UpsertHighlightRequest {
  return { id: h.id, title: h.title, sourceType: h.sourceType, sourceId: h.sourceId, isPublic: !h.isPublic };
}

export function AddResultToPortfolio({ sessionId }: { sessionId: string }) {
  const { data: highlights } = useGetMyHighlightsQuery();
  const [add, { isLoading: adding }] = useAddResultToPortfolioMutation();
  const [upsert, { isLoading: saving }] = useUpsertHighlightMutation();
  const [added, setAdded] = useState<PortfolioHighlight | null>(null);
  const [error, setError] = useState<string | null>(null);

  const existing = highlights?.find((h) => h.sourceType === 'conversation_result' && h.sourceId === sessionId);
  const item = added ?? existing;

  const onAdd = async () => {
    setError(null);
    try {
      setAdded(await add({ sessionId }).unwrap());
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? 'Only an evaluated result can be added to your portfolio.' : 'Could not add this result to your portfolio.');
    }
  };

  const onTogglePublish = async (target: PortfolioHighlight) => {
    setError(null);
    try {
      const updated = await upsert(publishToggleRequest(target)).unwrap();
      setAdded(updated);
    } catch {
      setError('Could not change whether this item is published.');
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 p-4" data-testid="add-result-to-portfolio">
      {!item ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-700">Keep this result in your portfolio. It stays private until you publish it.</p>
          <button type="button" onClick={onAdd} disabled={adding} className="btn-secondary text-sm py-1.5 px-4">
            {adding ? 'Adding…' : ADD_TO_PORTFOLIO_LABEL}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-700" data-state={item.isPublic ? 'published' : 'private'}>
              {item.isPublic ? PUBLISHED_ITEM_NOTE : PRIVATE_ITEM_NOTE}
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={item.isPublic}
                disabled={saving}
                onChange={() => onTogglePublish(item)}
                aria-label="Publish on my portfolio"
              />
              Publish on my portfolio
            </label>
          </div>
          <p className="text-xs text-gray-500">
            {DISCOVERY_NOTE}{' '}
            <Link to="/portfolio" className="text-primary-600 hover:text-primary-700">Manage in Portfolio</Link>
          </p>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-amber-800" role="alert">{error}</p>}
    </section>
  );
}

export default AddResultToPortfolio;
